import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { cancelJobMutation } from 'api/mutations/jobs';
import { addNegativeToast, addPositiveToast, errorIdentifier } from 'modules/toastContainer';
import { useJobManagerTranslations } from './useTranslations';

type CallbackFn = () => void;

type CancelJob = {
  id: number;
  showSuccessToaster?: boolean;
  /** Do the same action whether the operation is a success or not */
  onComplete?: CallbackFn;
  onSuccess?: CallbackFn;
  onError?: CallbackFn;
};

const source = 'useCancelJobs';

export const useCancelJobs = ({
  id,
  showSuccessToaster = true,
  onComplete,
  onSuccess,
  onError,
}: CancelJob): [() => Promise<{ isSuccess: boolean }>, boolean] => {
  const { t } = useJobManagerTranslations();
  const dispatch = useDispatch();
  const onSuccessCallback = onSuccess || onComplete;
  const onErrorCallback = onError || onComplete;
  const errorMessage = `Failed to cancel job id ${id}`;
  const errorToasterMessage = t('errorMessage.cancelJob');
  const successToasterMessage = t('successMessage.cancelJob');

  const errorHandler = (error: unknown) => {
    if (onErrorCallback) onErrorCallback();
    dispatch(
      addNegativeToast(
        errorToasterMessage,
        errorIdentifier({
          errorMessage,
          source,
          error,
        }),
      ),
    );
  };

  const [cancelJob, { loading }] = useMutation(cancelJobMutation, {
    onError: errorHandler,
  });

  const handleCancelJob = async () => {
    let isSuccess = true;
    const { data } = await cancelJob({
      variables: { id },
    });

    if (data?.cancelJob?.id) {
      if (onSuccessCallback) onSuccessCallback();
      if (showSuccessToaster) {
        dispatch(addPositiveToast(successToasterMessage));
      }
    }

    if (!data) {
      isSuccess = false;
    }

    return { isSuccess };
  };

  return [handleCancelJob, loading];
};
